// src/utils/httpClient.ts
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

// Types - ApiResponse removed as we now return data directly

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

class HttpClient {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    // Create axios instance with base configuration
    this.axiosInstance = axios.create({
      baseURL:
        process.env.REACT_APP_API_BASE_URL,
      timeout: 10000, // 10 seconds
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request Interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add access token to Authorization header
        const accessToken = localStorage.getItem("authToken");
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Return the actual response data instead of the full AxiosResponse
        return response.data;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Auth endpoints that should NOT trigger token refresh
        const authEndpoints = [
          '/auth/login',
          '/forgot-password',
          '/verify-otp',
          '/reset-password',
          '/auth/register',
          '/auth/verify',
        ];

        const isAuthEndpoint = authEndpoints.some(endpoint =>
          originalRequest.url?.includes(endpoint)
        );

        // Handle 401 Unauthorized errors (but skip for auth endpoints)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          if (this.isRefreshing) {
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem("refreshToken");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            // Call refresh token endpoint
            const response = await axios.post<RefreshTokenResponse>(
              `${
                process.env.REACT_APP_API_BASE_URL ||
                "http://localhost:5000/api"
              }/auth/refresh`,
              { refreshToken },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            } = response.data;

            // Update tokens in localStorage
            localStorage.setItem("authToken", newAccessToken);
            localStorage.setItem("refreshToken", newRefreshToken);

            // Update the original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }

            // Process failed queue
            this.processQueue(null, newAccessToken);

            // Retry the original request
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens
            this.processQueue(refreshError, null);
            this.clearTokens();
            // Don't redirect here, let the component handle it
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle other errors
        return this.handleError(error);
      }
    );
  }

  private processQueue(error: any, token: string | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  private clearTokens(): void {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
  }

  private handleError(error: AxiosError): Promise<never> {
    let errorMessage = "An unexpected error occurred";

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const errorData = data as any;

      // Try to extract the specific error message from backend
      // Backend can send errors in various formats: { error: { message: "..." } }, { error: "message" }, { message: "message" }, or { details: "message" }
      const backendMessage = errorData?.error?.message || errorData?.error || errorData?.message || errorData?.details;

      if (backendMessage && typeof backendMessage === 'string') {
        errorMessage = backendMessage;
      } else {
        // Fallback to generic messages based on status code
        switch (status) {
          case 400:
            errorMessage = backendMessage || "Bad Request - Please check your input";
            break;
          case 401:
            errorMessage = backendMessage || "Invalid credentials";
            break;
          case 403:
            errorMessage = backendMessage || "Forbidden - You do not have permission";
            break;
          case 404:
            errorMessage = backendMessage || "User not found";
            break;
          case 422:
            errorMessage = backendMessage || "Validation Error - Please check your data";
            break;
          case 500:
            errorMessage = backendMessage || "Server Error - Please try again later";
            break;
          default:
            errorMessage = backendMessage || `Server Error (${status})`;
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = "Network error. Please check your connection.";
    } else {
      // Something else happened
      errorMessage = error.message || "An unexpected error occurred";
    }

    console.error("HTTP Client Error:", {
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config,
    });

    return Promise.reject(new Error(errorMessage));
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return (await this.axiosInstance.get<T>(url, config)) as T;
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return (await this.axiosInstance.post<T>(url, data, config)) as T;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return (await this.axiosInstance.put<T>(url, data, config)) as T;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return (await this.axiosInstance.patch<T>(url, data, config)) as T;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return (await this.axiosInstance.delete<T>(url, config)) as T;
  }

  // Utility methods
  setBaseURL(baseURL: string): void {
    this.axiosInstance.defaults.baseURL = baseURL;
  }

  setDefaultHeader(key: string, value: string): void {
    this.axiosInstance.defaults.headers.common[key] = value;
  }

  removeDefaultHeader(key: string): void {
    delete this.axiosInstance.defaults.headers.common[key];
  }

  // Get the underlying axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Create and export singleton instance
const httpClient = new HttpClient();
export default httpClient;

// Export types for use in other files
export type { RefreshTokenResponse };
